<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-s-order" title="调研管理" subtitle="查看已发送的调研记录和员工反馈" />

    <div class="toolbar">
      <el-input v-model="search" placeholder="搜索员工姓名" prefix-icon="el-icon-search" clearable style="width: 280px;" />
      <el-radio-group v-model="tab" size="small" style="margin-left: auto;">
        <el-radio-button label="surveys">发送记录</el-radio-button>
        <el-radio-button label="responses">反馈记录</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 发送记录 -->
    <div v-if="tab === 'surveys'" class="table-wrapper">
      <template v-if="filteredSurveys.length > 0">
        <el-table :data="pagedSurveys" stripe class="data-table">
          <el-table-column prop="survey_id" label="ID" width="80" />
          <el-table-column prop="employee_name" label="员工" width="120" />
          <el-table-column prop="link" label="链接" min-width="260" show-overflow-tooltip />
          <el-table-column prop="created_at" label="发送时间" width="160" />
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'submitted' ? 'success' : 'info'" size="mini">{{ scope.row.status === 'submitted' ? '已提交' : '待提交' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="scope">
              <el-button v-if="$hasPermission('survey:delete')" type="text" class="action-disable" @click="removeSurvey(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-bar">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :total="filteredSurveys.length"
            :page-size="surveyPageSize"
            :current-page="surveyPage"
            :page-sizes="[10, 20, 50]"
            @size-change="handleSurveySizeChange"
            @current-change="handleSurveyPageChange"
          />
        </div>
      </template>
      <EmptyState v-else icon="el-icon-s-order" title="暂无发送记录" hint="前往员工列表发送调研问卷，发送记录将显示在此处">
        <template #action>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="$router.push('/admin/employees')">前往员工列表</el-button>
        </template>
      </EmptyState>
    </div>

    <!-- 反馈记录 -->
    <div v-if="tab === 'responses'" class="table-wrapper">
      <template v-if="filteredResponses.length > 0">
        <el-table :data="pagedResponses" stripe class="data-table">
          <el-table-column prop="response_id" label="ID" width="80" />
          <el-table-column prop="employee_name" label="员工" width="120" />
          <el-table-column prop="reason" label="离职原因" min-width="160" show-overflow-tooltip />
          <el-table-column prop="behaviors_confirmed" label="确认行为" min-width="160">
            <template slot-scope="scope">
              <el-tag v-for="(b, i) in scope.row.behaviors_confirmed.slice(0, 2)" :key="i" size="mini" style="margin-right: 4px; margin-bottom: 2px;">{{ b }}</el-tag>
              <span v-if="scope.row.behaviors_confirmed.length > 2" style="font-size: 12px; color: var(--color-text-muted);">+{{ scope.row.behaviors_confirmed.length - 2 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="suggestion" label="主观建议" min-width="200" show-overflow-tooltip />
          <el-table-column prop="submitted_at" label="提交时间" width="160" />
          <el-table-column label="操作" width="160">
            <template slot-scope="scope">
              <el-button type="text" class="action-edit" @click="viewResponseDetail(scope.row)">查看详情</el-button>
              <el-button v-if="$hasPermission('survey:delete')" type="text" class="action-disable" @click="removeResponse(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-bar">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :total="filteredResponses.length"
            :page-size="responsePageSize"
            :current-page="responsePage"
            :page-sizes="[10, 20, 50]"
            @size-change="handleResponseSizeChange"
            @current-change="handleResponsePageChange"
          />
        </div>
      </template>
      <EmptyState v-else icon="el-icon-chat-dot-square" title="暂无反馈记录" hint="员工提交调研反馈后，记录将显示在此处" />
    </div>

    <!-- 反馈详情弹窗 -->
    <el-dialog title="调研反馈详情" :visible.sync="detailVisible" width="560px" :append-to-body="true">
      <div v-if="detailRow" class="detail-content">
        <div class="detail-row">
          <span class="detail-label">员工姓名：</span>
          <span class="detail-value">{{ detailRow.employee_name }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">提交时间：</span>
          <span class="detail-value">{{ detailRow.submitted_at }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">离职原因：</span>
          <p class="detail-text">{{ detailRow.reason || '未填写' }}</p>
        </div>
        <div class="detail-row">
          <span class="detail-label">确认的行为特征：</span>
          <div class="detail-tags">
            <el-tag v-for="(b, i) in detailRow.behaviors_confirmed" :key="i" size="small" style="margin: 2px;">{{ b }}</el-tag>
            <span v-if="!detailRow.behaviors_confirmed || detailRow.behaviors_confirmed.length === 0" class="text-muted">未勾选任何行为</span>
          </div>
        </div>
        <div class="detail-row">
          <span class="detail-label">主观建议 / 补充说明：</span>
          <p class="detail-text">{{ detailRow.suggestion || '未填写' }}</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import EmptyState from '../../components/EmptyState.vue'
import { filterByDataScope } from '../../utils/permission'

export default {
  name: 'SurveyManage',
  components: { PageHeader, EmptyState },
  data() {
    return {
      search: '',
      tab: 'surveys',
      detailVisible: false,
      detailRow: null,
      surveyPage: 1,
      surveyPageSize: 10,
      responsePage: 1,
      responsePageSize: 10
    }
  },
  computed: {
    surveys() {
      const taskId = this.$store.getters.currentTaskId
      const all = this.$store.state.surveys.surveys || []
      if (!taskId) return all
      return all.filter(s => s.task_id === taskId || !s.task_id)
    },
    responses() {
      const taskId = this.$store.getters.currentTaskId
      const all = this.$store.state.surveys.responses || []
      if (!taskId) return all
      return all.filter(r => r.task_id === taskId || !r.task_id)
    },
    filteredSurveys() {
      let data = this.surveys
      if (this.search) {
        const k = this.search.toLowerCase()
        data = data.filter(s => (s.employee_name || '').toLowerCase().includes(k))
      }
      return filterByDataScope(this.$store, data, { nameField: 'employee_name' })
    },
    pagedSurveys() {
      const start = (this.surveyPage - 1) * this.surveyPageSize
      return this.filteredSurveys.slice(start, start + this.surveyPageSize)
    },
    filteredResponses() {
      let data = this.responses
      if (this.search) {
        const k = this.search.toLowerCase()
        data = data.filter(r => (r.employee_name || '').toLowerCase().includes(k))
      }
      return filterByDataScope(this.$store, data, { nameField: 'employee_name' })
    },
    pagedResponses() {
      const start = (this.responsePage - 1) * this.responsePageSize
      return this.filteredResponses.slice(start, start + this.responsePageSize)
    }
  },
  methods: {
    handleSurveySizeChange(size) { this.surveyPageSize = size; this.surveyPage = 1 },
    handleSurveyPageChange(page) { this.surveyPage = page },
    handleResponseSizeChange(size) { this.responsePageSize = size; this.responsePage = 1 },
    handleResponsePageChange(page) { this.responsePage = page },
    removeSurvey(row) {
      this.$confirm('确定删除此发送记录？', '提示', { type: 'warning' }).then(() => {
        this.$store.commit('surveys/DELETE_SURVEY', row.survey_id)
        this.$message.success('已删除')
      }).catch(() => {})
    },
    viewResponseDetail(row) {
      this.detailRow = row
      this.detailVisible = true
    },
    removeResponse(row) {
      this.$confirm('确定删除此反馈记录？', '提示', { type: 'warning' }).then(() => {
        this.$store.commit('surveys/DELETE_RESPONSE', row.response_id)
        this.$message.success('已删除')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }
.action-edit { color: var(--color-primary) !important; }
.action-disable { color: var(--color-high) !important; }
.pagination-bar { display: flex; justify-content: flex-end; padding: var(--space-md) var(--space-lg); background: var(--color-surface); border-top: 1px solid var(--color-border); }
.detail-content { padding: 0 10px; }
.detail-row { margin-bottom: 16px; }
.detail-label { font-size: 13px; font-weight: 600; color: var(--color-text-secondary); }
.detail-value { font-size: 14px; color: var(--color-text); }
.detail-tags { margin-top: 6px; }
.detail-text { margin: 6px 0 0 0; font-size: 14px; color: var(--color-text-secondary); line-height: 1.6; background: var(--color-bg); padding: 10px; border-radius: var(--radius-md); }
.text-muted { color: var(--color-text-muted); font-size: 13px; }
</style>
